import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import { Spin, Tooltip } from "antd";
import { FileImageOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { getPetrifilmImageFileNameFromObjectKey } from "./Helpers";
import {
  PetrifilmUploadActivityModal,
  PetrifilmCameraModal,
  PetrifilmImagePreviewModal,
} from "./PetrifilmImageModals";
import "./PetrifilmImageSection.css";

/** If the browser omits `file.type`, allow typical photo extensions only (format rule, no size cap). */
const LIKELY_IMAGE_EXTENSION = /\.(jpe?g|png|webp|bmp|ico|heic|heif|avif|tiff?)$/i;

const PETRIFILM_UPLOAD_ACTIVITY_HELP = "Choose a file or take a photo, then click Submit to save the petrifilm image.";
const PETRIFILM_UPLOAD_ACTIVITY_HELP_OPTIONAL = "Choose a file or take a photo and click Submit to save, or close if you do not have an image.";
const PETRIFILM_IMAGE_ACCEPT = "image/*,.png, .jpg, .jpeg,.svg";
const HELP_ADD_PETRIFILM_IMAGE = "To add an image, use the upload window that opens after you submit raw counts.";
const HELP_REPLACE_PETRIFILM_IMAGE = "To replace the current image, use the upload window that opens after you submit raw counts.";
const HELP_VIEW_ONLY_PETRIFILM_IMAGE = "Petrifilm image on file for reference. Click the name below to view full size.";

/** Check if file is a likely image file (format rule, no size cap). */
const isLikelyImageFile = (file) => {
  if (!file || !(file instanceof File)) {
    return false;
  }
  if (file.type) {
    return file.type.startsWith("image/");
  }
  return LIKELY_IMAGE_EXTENSION.test(file.name || "");
};

const prefersNativeDeviceCamera = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches === true;
  const mobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
  return coarse || mobileUa;
};

/**
 * Petrifilm image: upload happens only in the row-triggered activity modal. Main area shows a
 * read-only preview when an image exists. Optional `existingPetrifilmImageUrlFromServer`:
 * replace via the activity modal only.
 */
const PetrifilmImageSection = ({
  canUploadImage,
  petrifilmImageMandatory = true,
  existingPetrifilmImageUrlFromServer,
  petrifilmPreviewLoading = false,
  storedPetrifilmObjectKey,
  pendingLocalPetrifilmObjectKey,
  onPetrifilmImageFileChange,
  petrifilmUploadActivityRequested = false,
  onPetrifilmUploadActivityRequestConsumed,
  onAfterUploadModalCloseCommit,
}) => {
  const fileInputRef = useRef(null);
  const cameraFileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [uploadActivityModalOpen, setUploadActivityModalOpen] = useState(false);
  const [uploadModalCommitLoading, setUploadModalCommitLoading] = useState(false);

  /** Revoke local preview URL when component unmounts. */
  useEffect(() => {
    const blobUrl = localPreviewUrl;
    return () => {
      if (blobUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [localPreviewUrl]);

  const revokeLocalPreview = useCallback(() => {
    setLocalPreviewUrl(null);
    setFileName("");
  }, []);

  /** Close upload activity modal and revoke local preview when user cannot upload image. */
  useEffect(() => {
    if (!canUploadImage) {
      setUploadActivityModalOpen(false);
      revokeLocalPreview();
      if (onPetrifilmImageFileChange) {
        onPetrifilmImageFileChange(null);
      }
      if (onPetrifilmUploadActivityRequestConsumed) {
        onPetrifilmUploadActivityRequestConsumed();
      }
    }
  }, [canUploadImage, onPetrifilmImageFileChange, onPetrifilmUploadActivityRequestConsumed, revokeLocalPreview]);

  useEffect(() => {
    if (!canUploadImage || !petrifilmUploadActivityRequested) {
      return;
    }
    setUploadActivityModalOpen(true);
  }, [petrifilmUploadActivityRequested, canUploadImage]);

  const displayImageUrl = localPreviewUrl || existingPetrifilmImageUrlFromServer || null;
  const hasPetrifilmPreview = Boolean(displayImageUrl);
  const previewLabelText = getPetrifilmImageFileNameFromObjectKey(pendingLocalPetrifilmObjectKey)
    || getPetrifilmImageFileNameFromObjectKey(storedPetrifilmObjectKey)
    || fileName
    || (hasPetrifilmPreview ? "Petrifilm image" : "");

  const setImageFromFile = useCallback((file) => {
    if (!file) return;
    if (!isLikelyImageFile(file)) {
      toast.error("Please choose an image file.");
      return;
    }
    setLocalPreviewUrl(URL.createObjectURL(file));
    setFileName(file.name || "image");
    if (onPetrifilmImageFileChange) {
      onPetrifilmImageFileChange(file);
    }
  }, [onPetrifilmImageFileChange]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImageFromFile(file);
  };

  const openNativeCameraFilePicker = useCallback(() => {
    const input = cameraFileInputRef.current;
    if (input) {
      input.click();
    }
  }, []);

  const handleTakePhoto = () => {
    if (petrifilmPreviewLoading || uploadModalCommitLoading) {
      return;
    }
    if (prefersNativeDeviceCamera()) {
      openNativeCameraFilePicker();
      return;
    }
    setCameraOpen(true);
  };

  const handleUploadActivityModalSubmit = async () => {
    if (petrifilmPreviewLoading || uploadModalCommitLoading) {
      return;
    }
    if (onAfterUploadModalCloseCommit) {
      setUploadModalCommitLoading(true);
      try {
        const outcome = await onAfterUploadModalCloseCommit();
        if (outcome && outcome.success === false) {
          return;
        }
      } finally {
        setUploadModalCommitLoading(false);
      }
    }
    setUploadActivityModalOpen(false);
    if (onPetrifilmUploadActivityRequestConsumed) {
      onPetrifilmUploadActivityRequestConsumed();
    }
  };

  const handleSkipPetrifilmUploadActivity = useCallback(() => {
    if (uploadModalCommitLoading) {
      return;
    }
    revokeLocalPreview();
    if (onPetrifilmImageFileChange) {
      onPetrifilmImageFileChange(null);
    }
    setUploadActivityModalOpen(false);
    if (onPetrifilmUploadActivityRequestConsumed) {
      onPetrifilmUploadActivityRequestConsumed();
    }
  }, [onPetrifilmImageFileChange, onPetrifilmUploadActivityRequestConsumed, revokeLocalPreview, uploadModalCommitLoading]);

  let mainHelpText = HELP_VIEW_ONLY_PETRIFILM_IMAGE;
  if (canUploadImage) {
    mainHelpText = hasPetrifilmPreview ? HELP_REPLACE_PETRIFILM_IMAGE : HELP_ADD_PETRIFILM_IMAGE;
  }

  return (
    <div className={`PetrifilmImageSection${canUploadImage || hasPetrifilmPreview ? "" : " PetrifilmImageSection--modalsOnly"}`}>
      {(canUploadImage || hasPetrifilmPreview) && (
        <>
          <div className="PetrifilmImageSection__Title">Petrifilm image</div>
          <div className="PetrifilmImageSection__Help">{mainHelpText}</div>
        </>
      )}
      {canUploadImage && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={PETRIFILM_IMAGE_ACCEPT}
            className="PetrifilmImageSection__HiddenInput"
            onChange={handleFileChange}
          />
          <input
            ref={cameraFileInputRef}
            type="file"
            accept={PETRIFILM_IMAGE_ACCEPT}
            capture="environment"
            className="PetrifilmImageSection__HiddenInput"
            onChange={handleFileChange}
          />
        </>
      )}
      {(canUploadImage || hasPetrifilmPreview) && (
        <div className="PetrifilmImageSection__Actions">
          {petrifilmPreviewLoading && !displayImageUrl && (
            <div className="PetrifilmImageSection__PreviewLoading">
              <Spin size="small" />
              <span className="PetrifilmImageSection__PreviewLoadingText">Loading image…</span>
            </div>
          )}
          {displayImageUrl && previewLabelText && (
            <span className="PetrifilmImageSection__PreviewRow">
              <Tooltip title={uploadModalCommitLoading || (petrifilmPreviewLoading && !displayImageUrl) ? "Please wait…" : "Click to view petrifilm image"}>
                <span className="PetrifilmImageSection__PreviewTooltipAnchor">
                  <button
                    type="button"
                    className="PetrifilmImageSection__PreviewTrigger"
                    title={previewLabelText || "View petrifilm image"}
                    disabled={uploadModalCommitLoading || (petrifilmPreviewLoading && !displayImageUrl)}
                    onClick={() => setImagePreviewOpen(true)}
                  >
                    <FileImageOutlined className="PetrifilmImageSection__PreviewIcon" aria-hidden />
                    <span className="PetrifilmImageSection__PreviewTriggerLabel">{previewLabelText}</span>
                  </button>
                </span>
              </Tooltip>
            </span>
          )}
        </div>
      )}

      {canUploadImage && (
        <>
          <PetrifilmUploadActivityModal
            open={uploadActivityModalOpen}
            onSubmit={handleUploadActivityModalSubmit}
            onSkip={handleSkipPetrifilmUploadActivity}
            petrifilmImageMandatory={petrifilmImageMandatory}
            submitButtonText="Submit"
            canSubmit={hasPetrifilmPreview || (
              (typeof storedPetrifilmObjectKey === "string" && storedPetrifilmObjectKey.trim() !== "")
              || (typeof pendingLocalPetrifilmObjectKey === "string" && pendingLocalPetrifilmObjectKey.trim() !== "")
            )}
            uploadModalCommitLoading={uploadModalCommitLoading}
            petrifilmPreviewLoading={petrifilmPreviewLoading}
            helpText={petrifilmImageMandatory ? PETRIFILM_UPLOAD_ACTIVITY_HELP : PETRIFILM_UPLOAD_ACTIVITY_HELP_OPTIONAL}
            fileInputRef={fileInputRef}
            canUploadImage={canUploadImage}
            displayImageUrl={displayImageUrl}
            previewLabelText={previewLabelText}
            fileName={fileName}
            localPreviewUrl={localPreviewUrl}
            existingPetrifilmImageUrlFromServer={existingPetrifilmImageUrlFromServer}
            onTakePhoto={handleTakePhoto}
            onOpenFullPreview={() => setImagePreviewOpen(true)}
          />
          <PetrifilmCameraModal
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCaptureSuccess={setImageFromFile}
            onWebcamUnavailable={openNativeCameraFilePicker}
          />
        </>
      )}

      <PetrifilmImagePreviewModal
        open={imagePreviewOpen}
        onCancel={() => setImagePreviewOpen(false)}
        displayImageUrl={displayImageUrl}
      />
    </div>
  );
};

export default PetrifilmImageSection;
